import {
  type DatabaseContainerType,
  getDatabaseContainerType,
  useAnalytics as getAnalyticsClient,
} from '@databases/analytics';
import { collabStateManager } from '@databases/collab-state-manager';
import { getActivationId } from '@databases/database-state';
import { type Database, type DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  addChangeToChangeset,
  type DatabaseChange,
  type DatabaseChangeset,
} from '@databases/utils/database/changesets';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { deserializeError } from '@databases/utils/utils';
import { debounce } from 'lodash-es';
import { type ThunkAction } from 'redux-thunk';

import { type DefaultRootState } from '../redux';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, never>;

const sendDatabaseCellContentHasBeenModifiedEventDebounced = debounce(
  async (
    activationId: string | undefined,
    contentId: string,
    containerType: DatabaseContainerType,
    fieldType: DatabaseFieldType
  ) => {
    const client = getAnalyticsClient();
    if (client !== undefined) {
      await client.sendDatabaseCellContentHasBeenModifiedEvent({
        activationId: activationId,
        contentId: contentId,
        databaseContainer: containerType,
        fieldType,
      });
    }
  },
  1000
);

const applyChangetoCollabDatabase =
  (database: Database, changeset: DatabaseChangeset): Dispatcher<void> =>
  (dispatch, getState) => {
    const collabManager = collabStateManager.getCollabManager(database.contentId);
    if (collabManager) {
      collabManager.applyChangeset(changeset, getState());
    }
  };

export const addChangeToDraft =
  (contentId: string, change: DatabaseChange): Dispatcher<void> =>
  (dispatch, getState) => {
    if (change.type === DatabaseChangeType.SET_VALUE && change.change?.type) {
      sendDatabaseCellContentHasBeenModifiedEventDebounced(
        getActivationId(),
        contentId,
        getDatabaseContainerType(),
        change.change.type
      );
    }

    const database = selectDatabaseByContentIdOptional(getState(), { contentId });
    if (!database) {
      throw new Error(`Database ${contentId} not found.`);
    } else if (isBrokenDatabase(database)) {
      throw deserializeError(database.error);
    }

    const changeset = addChangeToChangeset([], change);
    dispatch(applyChangetoCollabDatabase(database, changeset));
  };

export const setDraftedChangeset =
  (contentId: string, changeset: DatabaseChangeset): Dispatcher<void> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdOptional(getState(), { contentId });
    if (!database) {
      throw new Error(`Database ${contentId} not found.`);
    } else if (isBrokenDatabase(database)) {
      throw deserializeError(database.error);
    }

    dispatch(applyChangetoCollabDatabase(database, changeset));
  };
